/**
 * CR V3 TTS 统一音色模型（H5 / Android / PC Host 对齐）
 *
 * 方案 B：默认使用一对 MultilingualNeural（女 Ava / 男 Andrew），
 * 用 <lang xml:lang> 适配听语 locale。
 *
 * 例外：微软 Multilingual Group1 不支持的 locale（当前已知 ky-KG）
 * 回退到该语种专用 Neural，避免无声。
 *
 * @see https://learn.microsoft.com/azure/ai-services/speech-service/speech-synthesis-markup-voice
 */

export type TtsVoiceGender = 'female' | 'male'

/** 统一男女 Multilingual 音色（主 locale = en-US） */
export const TTS_MULTILINGUAL_VOICES = {
  female: 'en-US-AvaMultilingualNeural',
  male: 'en-US-AndrewMultilingualNeural',
} as const

/** Multilingual 音色的 speak/@xml:lang 主 locale */
export const TTS_VOICE_PRIMARY_LOCALE = 'en-US'

/** 默认播报性别（课堂听者 TTS；后续可接偏好配置） */
export const TTS_DEFAULT_GENDER: TtsVoiceGender = 'female'

/**
 * Multilingual 不覆盖、需专用音色的 locale（对照微软 multilingual-voices locale index）。
 * 短码与 BCP 均列入，便于兜底。
 */
const MULTILINGUAL_UNSUPPORTED_FALLBACK: Record<string, string> = {
  'ky-KG': 'ky-KG-GulzatNeural',
  ky: 'ky-KG-GulzatNeural',
}

export function escapeSsmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function normalizeLocaleKey(localeOrShort: string): string {
  return localeOrShort.trim()
}

/** 解析最终 voice name：优先 Multilingual，不支持则专用回退 */
export function resolveTtsVoiceName(
  localeOrShort: string,
  gender: TtsVoiceGender = TTS_DEFAULT_GENDER,
): string {
  const loc = normalizeLocaleKey(localeOrShort)
  if (!loc) {
    return TTS_MULTILINGUAL_VOICES[gender]
  }
  const fallback =
    MULTILINGUAL_UNSUPPORTED_FALLBACK[loc]
    ?? MULTILINGUAL_UNSUPPORTED_FALLBACK[loc.toLowerCase()]
    ?? Object.entries(MULTILINGUAL_UNSUPPORTED_FALLBACK).find(
      ([k]) => k.toLowerCase() === loc.toLowerCase(),
    )?.[1]
  if (fallback) {
    return fallback
  }
  return TTS_MULTILINGUAL_VOICES[gender]
}

export function isMultilingualVoice(voiceName: string): boolean {
  return voiceName.includes('Multilingual')
}

/**
 * 构建 SSML：
 * - Multilingual：speak xml:lang=en-US + voice + lang xml:lang=听语
 * - 专用回退：speak xml:lang=听语 + voice（无 lang 包裹）
 */
export function buildTtsSsml(
  text: string,
  languageCode2: string,
  playbackRate = 1,
  gender: TtsVoiceGender = TTS_DEFAULT_GENDER,
): string {
  const locale = normalizeLocaleKey(languageCode2) || 'en-US'
  const voice = resolveTtsVoiceName(locale, gender)
  const rate = playbackRate > 0 ? playbackRate : 1
  const body = `<prosody rate="${rate}">${escapeSsmlText(text)}</prosody>`

  if (isMultilingualVoice(voice)) {
    return (
      `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${TTS_VOICE_PRIMARY_LOCALE}">` +
      `<voice name="${voice}">` +
      `<lang xml:lang="${locale}">${body}</lang>` +
      `</voice>` +
      `</speak>`
    )
  }

  return (
    `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${locale}">` +
    `<voice name="${voice}">${body}</voice>` +
    `</speak>`
  )
}

/** @deprecated 使用 resolveTtsVoiceName；保留别名避免旧引用断裂 */
export function defaultNeuralVoiceForLocale(localeOrShort: string): string {
  return resolveTtsVoiceName(localeOrShort, TTS_DEFAULT_GENDER)
}
